import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Caption1,
  Field,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Textarea,
  makeStyles,
} from '@fluentui/react-components';
import { CSV_COLUMNS, planCsvImport } from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import { fetchCatalogOptions, importPeople, type ApiError } from '@/lib/api';

const useStyles = makeStyles({
  panel: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  issues: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'center' },
});

/**
 * Bulk import with a preview that cannot lie: the preview runs `planCsvImport` from
 * packages/shared, and the server runs the same function on the same input. Known roles
 * and tags come from the catalog, so a typo'd role is named here rather than producing a
 * person with an empty checklist.
 */
export function CsvImportPanel() {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [csv, setCsv] = useState('');
  const options = useQuery({ queryKey: ['catalog-options'], queryFn: fetchCatalogOptions, staleTime: Infinity });

  const plan = useMemo(() => {
    if (csv.trim() === '' || options.data === undefined) return null;
    return planCsvImport(csv, {
      knownProfiles: options.data.jobProfiles.map((p) => p.key),
      knownTags: options.data.tags.map((t) => t.key),
    });
  }, [csv, options.data]);

  const run = useMutation({
    mutationFn: importPeople,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['people'] });
      setCsv('');
    },
  });

  const error = run.error as (Error & Partial<ApiError>) | null;
  const blocked = plan === null || plan.errors.length > 0 || plan.rows.length === 0;

  return (
    <div className={styles.panel}>
      <Field
        label="Paste CSV"
        hint={
          `Columns: ${CSV_COLUMNS.join(', ')}. Separate multiple tags with a semicolon. ` +
          'A blank cell leaves that field alone — importing never clears something already recorded here.'
        }
      >
        <Textarea value={csv} onChange={(_, d) => setCsv(d.value)} rows={8} resize="vertical" />
      </Field>

      {plan === null ? null : plan.errors.length > 0 ? (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>Nothing will be imported until these are fixed</MessageBarTitle>
            <div className={styles.issues}>
              {plan.errors.slice(0, 12).map((issue) => (
                <Caption1 key={`${issue.line}-${issue.message}`}>
                  Line {issue.line}: {issue.message}
                </Caption1>
              ))}
              {plan.errors.length > 12 ? <Caption1>…and {plan.errors.length - 12} more.</Caption1> : null}
            </div>
          </MessageBarBody>
        </MessageBar>
      ) : (
        <MessageBar intent="success">
          <MessageBarBody>
            {plan.rows.length} row{plan.rows.length === 1 ? '' : 's'} ready to import.
          </MessageBarBody>
        </MessageBar>
      )}

      {error === null ? null : (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>{error.message}</MessageBarTitle>
            <div className={styles.issues}>
              {(error.issues ?? []).slice(0, 12).map((issue) => (
                <Caption1 key={issue}>{issue}</Caption1>
              ))}
            </div>
          </MessageBarBody>
        </MessageBar>
      )}

      {run.data === undefined ? null : (
        <MessageBar intent="success">
          <MessageBarBody>
            Imported {run.data.created} new, updated {run.data.updated} existing, left{' '}
            {run.data.unchanged} unchanged.
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.actions}>
        <Button appearance="primary" disabled={blocked || run.isPending} onClick={() => run.mutate(csv)}>
          Import
        </Button>
        <Caption1>
          Re-importing the same file updates people rather than duplicating them, and only
          the columns you filled in are touched.
        </Caption1>
      </div>
    </div>
  );
}
